import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import React from "react"
import { TimeHelper } from "../helpers/time"
import { useBearStore } from "../stores/user"
import type { IAppSheet } from "../types/appsheet"
import type { IBlog } from "../types/blog"
import type { CartResponse } from "../types/course/cart"
import type { ICheckout } from "../types/course/checkout"
import type { CourseDetail } from "../types/course/detail"
import type { IFooter } from "../types/footer"
import type { Blog, Data, ICourse, IPagination } from "../types/home"
import type { IOrder } from "../types/order"
import { axiosInstance } from "../axios"
import { getCookie, deleteCookie } from "cookies-next"
import type { ICartResponse } from "@/app/carts/CartClient"
import { useReferralStore } from "@/lib/stores/referralStore"
import { useDiscountStore } from "../stores/discount"
import type { IUtmParams, IReferralTrackingResponse, IReferralTrackingRequest } from "../types/referral"
import type { 
	IAffiliateBalanceResponse, 
	IWithdrawalRequest, 
	IWithdrawalCreateResponse, 
	IWithdrawalsResponse, 
	IWithdrawalsParams, 
	IWithdrawalDetailResponse 
} from "@/app/auth/profile/types/affiliate"

// The response interceptor is handled in lib/axios.ts for global error handling

export const useCurrentUser = () => {
	const { setProfileUser } = useBearStore()
	const { data: currentUser = [] } = useQuery({
		queryKey: ["currentUser"],
		queryFn: async () => {
				const response = await axiosInstance.get("/api/me")
				const userData = response.data.data
				setProfileUser(userData)
				return userData
		},
	})

	return currentUser
}

export const useHomeData = () => {
	const [enabled, setEnabled] = React.useState(false)

	const query = useQuery<Data>({
		queryKey: ["homeData"],
		queryFn: () => axiosInstance.get("/api/home").then((res) => res.data.data),
		enabled: enabled,
	})

	React.useEffect(() => {
		if (!enabled) {
			setEnabled(true)
		}
	}, [enabled])

	return query
}

export const useListCourseCategory = () => {
	return useQuery<Data>({
		queryKey: ["listCourseCategory"],
		queryFn: () =>
			axiosInstance.get("/api/list-categories").then((res) => res.data.data),
	})
}

export const useListCourseTags = () => {
	return useQuery<Data>({
		queryKey: ["listCourseTags"],
		queryFn: async () => {
			try {
				const res = await axiosInstance.get("/api/list-courses-tags")
				return res.data.data || []
			} catch (error) {
				console.error("Error fetching course tags:", error)
				return []
			}
		},
	})
}

export const useListProductTag = () => {
	return useQuery<Data>({
		queryKey: ["listProductTag"],
		queryFn: () =>
			axiosInstance
				.get("/api/products/list/category")
				.then((res) => res.data.data),
	})
}

export const useCourseDetail = (courseId: number) => {
	return useQuery<CourseDetail>({
		queryKey: ["courseDetail", courseId],
		queryFn: () =>
			axiosInstance
				.get(`/api/courses/${courseId}`)
				.then((res) => res.data.data),
	})
}

export const useBlogDetail = (blogId: number) => {
	return useQuery<IBlog>({
		queryKey: ["blogDetail", blogId],
		queryFn: () =>
			axiosInstance.get(`/api/blogs/${blogId}`).then((res) => res.data.data),
	})
}

export const useCourseCategory = () => {
	return useQuery<Array<IBlog>>({
		queryKey: ["CourseCategory"],
		queryFn: () =>
			axiosInstance.get("/api/list-categories").then((res) => res.data.data),
		staleTime: TimeHelper.oneHour(),
		gcTime: TimeHelper.oneHour(),
	})
}

export const useBlogCategory = () => {
	return useQuery<Array<IBlog>>({
		queryKey: ["BlogCategory"],
		queryFn: () =>
			axiosInstance
				.get("/api/blogs/list/category")
				.then((res) => res.data.data),
		staleTime: TimeHelper.oneHour(),
		gcTime: TimeHelper.oneHour(),
	})
}

export const useCheckout = (cartIds: Array<number>) => {
	const { setLastCheckout } = useBearStore()
	const lastCheckout = useBearStore.getState().lastCheckout
	const referrer = useReferralStore.getState().ref
	const discountCode = useDiscountStore.getState().discountCode
	
	return useQuery<ICheckout>({
		queryKey: ["checkout", cartIds],
		queryFn: () =>
			axiosInstance
				.post("/api/carts/checkout", {
					cart_id: cartIds,
					referrer: referrer || null,
					ma_giam_gia: discountCode || null
				})
				.then((res) => {
					setLastCheckout(res.data)
					return res.data
				})
				.catch((error) => {
					if (error.response && error.response.status === 422) {
						return lastCheckout
					}
					throw error
				}),
	})
}

export const useOrderDetail = (orderId: number) => {
	return useQuery<ICheckout>({
		queryKey: ["orderDetail", orderId],
		queryFn: () =>
			axiosInstance.post(`/api/orders/${orderId}`).then((res) => res.data),
	})
}

export const UseAddCart = (courseId: number) => {
	return useQuery<CourseDetail>({
		queryKey: ["courseDetail", courseId],
		queryFn: () =>
			axiosInstance
				.post("/api/carts", { course_id: courseId })
				.then((res) => res.data.data),
	})
}

export const useAppSheet = (slug: string) => {
	return useQuery<IAppSheet>({
		queryKey: ["appSheet", slug],
		queryFn: () =>
			axiosInstance.get(`/api/appsheet/${slug}`).then((res) => res.data),
	})
}

export const useMyCourse = () => {
	const { setMyCourses } = useBearStore()
	const bearToken = getCookie("auth-token")
	return useQuery<Array<CourseDetail>>({
		queryKey: ["myCourse"],
		queryFn: async () => {
			try {
				const res = await axiosInstance.get("/api/my-course", {
					headers: {
						Authorization: `Bearer ${bearToken}`,
					},
				})
				const courses = res.data.data
				setMyCourses(courses)
				return courses
			} catch (error) {
				console.error("Error fetching my courses:", error)
				throw error
			}
		},
	})
}

export const useMySoftware = () => {
	const bearToken = getCookie("auth-token")
	return useQuery({
		queryKey: ["mySoftware"],
		queryFn: async () => {
			try {
				if (!bearToken) {
					return { data: [] }
				}
				const res = await axiosInstance.get("/api/my-software", {
					headers: {
						Authorization: `Bearer ${bearToken}`,
					},
				})
				return res.data
			} catch (error) {
				console.error("Error fetching my software:", error)
				return { data: [] }
			}
		},
	})
}

export const useLogout = () => {
	return useMutation({
		mutationFn: async () => {
			const res = await axiosInstance.post("/api/logout")
			return res.data
		},
	})
}

export const useChangeInfo = () => {
	return useMutation({
		mutationFn: async (data: unknown) => {
			const res = await axiosInstance.post("/api/change-info", data)
			return res.data
		},
	})
}

export const useCart = () => {	
	const { setCarts } = useBearStore()
	const bearToken = getCookie("auth-token")
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["cart"],
		queryFn: async () => {
			try {
				if (!bearToken) {
					return { data: [] }
				}
				const res = await axiosInstance.get("/api/carts")
				const cartData = res.data
				setCarts(cartData.data)
				return cartData
			} catch (error: any) {
				// if (error.response?.status === 401) {
				// 	// Clear auth state
				// 	deleteCookie('auth-token')
				// 	// Reset user store
				// 	useBearStore.getState().reset()
				// 	// Redirect to login
				// 	window.location.href = '/auth/login/google?error=401'
				// }
				console.error("Error fetching cart:", error)
				return { data: [] }
			}
		},
	})

	return { data, isLoading, error, refetch }
}

export const useOrders = () => {
	const { setOrders } = useBearStore()
	const bearToken = getCookie("auth-token")
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["orders"],
		queryFn: async () => {
			try {
				if (!bearToken) {
					return { data: [] }
				}
				const res = await axiosInstance.get("/api/orders")
				const ordersData = res.data
				setOrders(ordersData.data)
				return ordersData
			} catch (error) {
				console.error("Error fetching orders:", error)
				return { data: [] }
			}
		},
	})

	return { data, isLoading, error, refetch }
}

export const useFooter = () => {
	const { setFooterMenu } = useBearStore()
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["footer"],
		queryFn: async () => {
			try {
				const res = await axiosInstance.get("/api/pagesStatic")
				const footerData = res.data
				setFooterMenu(footerData)
				return footerData
			} catch (error) {
				console.error("Error fetching footer:", error)
				return { data: [] }
			}
		},
	})

	return { data, isLoading, error, refetch }
}

export const useLecturerCourses = (lecturerId: string | number, page = 1) =>
	useQuery({
		queryKey: ["lecturerCourses", lecturerId, page],
		queryFn: async () => {
			try {
				const res = await axiosInstance.get(`/api/lecturers/${lecturerId}/courses`, {
					params: { page }
				})
				return {
					courses: res.data.data,
					paginate: res.data.paginate,
				}
			} catch (error) {
				console.error("Error fetching lecturer courses:", error)
				return { courses: [], paginate: { total: 0, per_page: 10, current_page: 1 } }
			}
		},
		enabled: !!lecturerId,
	})

export const useLecturerBlogs = (lecturerId: string | number, page = 1) =>
	useQuery({
		queryKey: ["lecturerBlogs", lecturerId, page],
		queryFn: async () => {
			try {
				const res = await axiosInstance.get(`/api/lecturers/${lecturerId}/blogs`, {
					params: { page }
				})
				return {
					blogs: res.data.data,
					paginate: res.data.paginate,
				}
			} catch (error) {
				console.error("Error fetching lecturer blogs:", error)
				return { blogs: [], paginate: { total: 0, per_page: 10, current_page: 1 } }
			}
		},
		enabled: !!lecturerId,
	})

export const createPendingOrder = async (courseId: number) => {
	try {
		const response = await axiosInstance.get(`/api/orders/pending/${courseId}`)
		return response.data
	} catch (error) {
		console.error("Error creating pending order:", error)
		throw error
	}
}

export const useCartCount = () => {
	const bearToken = getCookie("auth-token")
	const queryClient = useQueryClient()
	
	return useQuery({
		queryKey: ["cartCount"],
		queryFn: async () => {
			try {
				if (!bearToken) {
					return 0
				}
				
				// Try to get cart data from cache first
				const cachedCartData = queryClient.getQueryData<ICartResponse>(["cart"])
				if (cachedCartData?.paginate?.total !== undefined) {
					return cachedCartData.paginate.total
				}
				
				// If no cached data, fetch from API
				const res = await axiosInstance.get<ICartResponse>("/api/carts")
				return res.data.paginate.total
			} catch (error) {
				console.error("Error fetching cart count:", error)
				return 0
			}
		},
		staleTime: 30 * 1000, // 30 seconds
		gcTime: 5 * 60 * 1000, // 5 minutes
	})
}

export const useAddToCart = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (courseId: number) => {
			const response = await axiosInstance.post("/api/carts", { course_id: courseId })
			return response.data
		},
		onSuccess: () => {
			// Invalidate cart-related queries to trigger refetch
			queryClient.invalidateQueries({ queryKey: ["cart"] })
			queryClient.invalidateQueries({ queryKey: ["cartCount"] })
		},
	})
}

export const useRemoveFromCart = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (cartId: number) => {
			const response = await axiosInstance.delete(`/api/carts/${cartId}`)
			return response.data
		},
		onSuccess: () => {
			// Invalidate cart-related queries to trigger refetch
			queryClient.invalidateQueries({ queryKey: ["cart"] })
			queryClient.invalidateQueries({ queryKey: ["cartCount"] })
		},
	})
}

export const useWhitelistMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async ({ id, type, isWhitelist }: { id: number; type: string; isWhitelist: boolean }) => {
			const response = await axiosInstance.post("/api/whitelist", {
				type,
				item_id: id,
				is_active: isWhitelist ? 0 : 1,
			})
			return response.data
		},
		onSuccess: () => {
			// Invalidate whitelist-related queries if needed
			queryClient.invalidateQueries({ queryKey: ["whitelist"] })
		},
	})
}

/**
 * Tracks a referral click by sending data to the affiliate tracking API
 * 
 * @param code - The referral/affiliate code to track
 * @param utmParams - Optional UTM parameters for campaign tracking
 * @returns Promise resolving to the API response
 * 
 * @example
 * ```typescript
 * // Basic tracking
 * await trackReferralClick('partner123')
 * 
 * // With UTM parameters
 * await trackReferralClick('partner123', {
 *   utm_source: 'facebook',
 *   utm_medium: 'social',
 *   utm_campaign: 'summer2024'
 * })
 * ```
 */
export const trackReferralClick = async (
	code: string,
	utmParams?: IUtmParams
): Promise<IReferralTrackingResponse> => {
	try {
		// Prepare query parameters
		const queryParams = new URLSearchParams()
		if (utmParams?.utm_source) queryParams.append("utm_source", utmParams.utm_source)
		if (utmParams?.utm_medium) queryParams.append("utm_medium", utmParams.utm_medium)
		if (utmParams?.utm_campaign) queryParams.append("utm_campaign", utmParams.utm_campaign)
		if (utmParams?.utm_term) queryParams.append("utm_term", utmParams.utm_term)
		if (utmParams?.utm_content) queryParams.append("utm_content", utmParams.utm_content)

		// Prepare headers with browser information
		const headers: Record<string, string> = {}
		
		// Only add browser-specific headers if we're in the browser
		if (typeof window !== "undefined") {
			headers["User-Agent"] = navigator.userAgent
			
			// Add referer if available
			if (document.referrer) {
				headers.Referer = document.referrer
			}
		}

		// Make the tracking API call
		const queryString = queryParams.toString()
		const url = `/api/affiliate/track/${code}${queryString ? `?${queryString}` : ""}`

		const response = await axiosInstance.post<IReferralTrackingResponse>(url, {}, {
			headers
		})

		return response.data
	} catch (error) {
		console.error("Failed to track referral click:", error)
		throw error
	}
}

/**
 * React Query hook for tracking referral clicks with automatic error handling
 * 
 * @returns Mutation object with mutate function and loading/error states
 * 
 * @example
 * ```typescript
 * const trackReferral = useTrackReferral()
 * 
 * // Track a referral
 * trackReferral.mutate({
 *   code: 'partner123',
 *   utmParams: { utm_source: 'facebook' }
 * })
 * 
 * // Check loading state
 * if (trackReferral.isPending) {
 *   return <div>Tracking...</div>
 * }
 * ```
 */
export const useTrackReferral = () => {
	return useMutation({
		mutationFn: ({ code, utmParams }: IReferralTrackingRequest) => 
			trackReferralClick(code, utmParams),
		onSuccess: (data: IReferralTrackingResponse) => {
			console.log("Referral tracking successful:", data)
		},
		onError: (error) => {
			console.error("Referral tracking failed:", error)
		}
	})
}

// ==================== AFFILIATE WITHDRAWAL MANAGEMENT ====================

/**
 * Get affiliate balance summary including total commission, available balance, etc.
 * 
 * @returns Promise resolving to the balance summary API response
 * 
 * @example
 * ```typescript
 * const balance = await getAffiliateBalance()
 * console.log(balance.data.available_balance)
 * ```
 */
export const getAffiliateBalance = async (): Promise<IAffiliateBalanceResponse> => {
	try {
		const response = await axiosInstance.get<IAffiliateBalanceResponse>("/api/affiliate/balance")
		return response.data
	} catch (error) {
		console.error("Failed to fetch affiliate balance:", error)
		throw error
	}
}

/**
 * React Query hook for fetching affiliate balance summary
 * 
 * @returns Query object with balance data and loading/error states
 * 
 * @example
 * ```typescript
 * const { data: balance, isLoading, error } = useAffiliateBalance()
 * 
 * if (isLoading) return <div>Loading...</div>
 * if (error) return <div>Error loading balance</div>
 * 
 * return <div>Available: {balance?.data.formatted_available_balance}</div>
 * ```
 */
export const useAffiliateBalance = () => {
	return useQuery({
		queryKey: ["affiliateBalance"],
		queryFn: getAffiliateBalance,
		staleTime: 5 * 60 * 1000, // 5 minutes in milliseconds
		gcTime: 10 * 60 * 1000, // 10 minutes in milliseconds
	})
}

/**
 * Create a withdrawal request
 * 
 * @param request - The withdrawal request data
 * @returns Promise resolving to the created withdrawal response
 * 
 * @example
 * ```typescript
 * const withdrawal = await createWithdrawalRequest({
 *   amount: 600000,
 *   bank_name: "Vietcombank",
 *   bank_account_number: "1234567890123",
 *   bank_account_name: "NGUYEN VAN A",
 *   bank_branch: "Chi nhánh Hà Nội",
 *   user_note: "Rút tiền hoa hồng tháng 12"
 * })
 * ```
 */
export const createWithdrawalRequest = async (request: IWithdrawalRequest): Promise<IWithdrawalCreateResponse> => {
	try {
		const response = await axiosInstance.post<IWithdrawalCreateResponse>("/api/affiliate/withdrawals", request)
		return response.data
	} catch (error) {
		console.error("Failed to create withdrawal request:", error)
		throw error
	}
}

/**
 * React Query mutation hook for creating withdrawal requests
 * 
 * @returns Mutation object with mutate function and loading/error states
 * 
 * @example
 * ```typescript
 * const createWithdrawal = useCreateWithdrawalRequest()
 * 
 * const handleSubmit = (data) => {
 *   createWithdrawal.mutate(data, {
 *     onSuccess: (response) => {
 *       toast.success(response.message)
 *     },
 *     onError: (error) => {
 *       toast.error(error.message)
 *     }
 *   })
 * }
 * ```
 */
export const useCreateWithdrawalRequest = () => {
	const queryClient = useQueryClient()
	
	return useMutation({
		mutationFn: createWithdrawalRequest,
		onSuccess: () => {
			// Invalidate related queries to refresh data
			queryClient.invalidateQueries({ queryKey: ["affiliateBalance"] })
			queryClient.invalidateQueries({ queryKey: ["withdrawalRequests"] })
		},
		onError: (error) => {
			console.error("Withdrawal request creation failed:", error)
		}
	})
}

/**
 * Get withdrawal requests with optional filters
 * 
 * @param params - Optional query parameters for filtering
 * @returns Promise resolving to the withdrawals list response
 * 
 * @example
 * ```typescript
 * // Get all withdrawals
 * const withdrawals = await getWithdrawalRequests()
 * 
 * // Get withdrawals with filters
 * const filteredWithdrawals = await getWithdrawalRequests({
 *   page: 1,
 *   per_page: 10,
 *   status: 0,
 *   date_from: '2025-01-01',
 *   date_to: '2025-01-31'
 * })
 * ```
 */
export const getWithdrawalRequests = async (params?: IWithdrawalsParams): Promise<IWithdrawalsResponse> => {
	try {
		// Build query parameters
		const queryParams = new URLSearchParams()
		
		if (params?.page !== undefined) {
			queryParams.append("page", params.page.toString())
		}
		if (params?.per_page !== undefined) {
			queryParams.append("per_page", params.per_page.toString())
		}
		if (params?.status !== undefined) {
			queryParams.append("status", params.status.toString())
		}
		if (params?.date_from) {
			queryParams.append("date_from", params.date_from)
		}
		if (params?.date_to) {
			queryParams.append("date_to", params.date_to)
		}

		const queryString = queryParams.toString()
		const url = `/api/affiliate/withdrawals${queryString ? `?${queryString}` : ""}`

		const response = await axiosInstance.get<IWithdrawalsResponse>(url)
		return response.data
	} catch (error) {
		console.error("Failed to fetch withdrawal requests:", error)
		throw error
	}
}

/**
 * React Query hook for fetching withdrawal requests
 * 
 * @param params - Optional query parameters for filtering
 * @returns Query object with withdrawals data and loading/error states
 * 
 * @example
 * ```typescript
 * const { data: withdrawals, isLoading, error } = useWithdrawalRequests({
 *   page: 1,
 *   per_page: 10,
 *   status: 0
 * })
 * 
 * if (isLoading) return <div>Loading withdrawals...</div>
 * if (error) return <div>Error loading withdrawals</div>
 * 
 * return (
 *   <div>
 *     {withdrawals?.data.withdrawals.map(withdrawal => (
 *       <div key={withdrawal.id}>{withdrawal.request_code}</div>
 *     ))}
 *   </div>
 * )
 * ```
 */
export const useWithdrawalRequests = (params?: IWithdrawalsParams) => {
	return useQuery({
		queryKey: ["withdrawalRequests", params],
		queryFn: () => getWithdrawalRequests(params),
		staleTime: 1 * 60 * 1000, // 1 minute in milliseconds
		gcTime: 5 * 60 * 1000, // 5 minutes in milliseconds
	})
}

/**
 * Get detailed information about a specific withdrawal request
 * 
 * @param withdrawalId - The ID of the withdrawal request
 * @returns Promise resolving to the withdrawal detail response
 * 
 * @example
 * ```typescript
 * const withdrawalDetail = await getWithdrawalDetail(1)
 * console.log(withdrawalDetail.data.withdrawal.request_code)
 * console.log(withdrawalDetail.data.audit_logs)
 * ```
 */
export const getWithdrawalDetail = async (withdrawalId: number): Promise<IWithdrawalDetailResponse> => {
	try {
		const response = await axiosInstance.get<IWithdrawalDetailResponse>(`/api/affiliate/withdrawals/${withdrawalId}`)
		return response.data
	} catch (error) {
		console.error("Failed to fetch withdrawal detail:", error)
		throw error
	}
}

/**
 * React Query hook for fetching withdrawal request details
 * 
 * @param withdrawalId - The ID of the withdrawal request
 * @returns Query object with withdrawal detail data and loading/error states
 * 
 * @example
 * ```typescript
 * const { data: withdrawalDetail, isLoading, error } = useWithdrawalDetail(1)
 * 
 * if (isLoading) return <div>Loading withdrawal details...</div>
 * if (error) return <div>Error loading withdrawal details</div>
 * 
 * return (
 *   <div>
 *     <h3>{withdrawalDetail?.data.withdrawal.request_code}</h3>
 *     <p>Amount: {withdrawalDetail?.data.withdrawal.formatted_amount}</p>
 *     <p>Status: {withdrawalDetail?.data.withdrawal.status_text}</p>
 *   </div>
 * )
 * ```
 */
export const useWithdrawalDetail = (withdrawalId: number) => {
	return useQuery({
		queryKey: ["withdrawalDetail", withdrawalId],
		queryFn: () => getWithdrawalDetail(withdrawalId),
		enabled: !!withdrawalId,
		staleTime: 1 * 60 * 1000, // 1 minute in milliseconds
		gcTime: 5 * 60 * 1000, // 5 minutes in milliseconds
	})
}

/**
 * Cancel a withdrawal request
 * 
 * @param withdrawalId - The ID of the withdrawal request to cancel
 * @returns Promise resolving to the cancellation response
 * 
 * @example
 * ```typescript
 * const result = await cancelWithdrawalRequest(1)
 * console.log(result.message) // "Yêu cầu rút tiền đã được hủy thành công"
 * ```
 */
export const cancelWithdrawalRequest = async (withdrawalId: number): Promise<{ success: boolean; message: string }> => {
	try {
		const response = await axiosInstance.post<{ success: boolean; message: string }>(`/api/affiliate/withdrawals/${withdrawalId}/cancel`)
		return response.data
	} catch (error) {
		console.error("Failed to cancel withdrawal request:", error)
		throw error
	}
}

/**
 * React Query mutation hook for canceling withdrawal requests
 * 
 * @returns Mutation object with mutate function and loading/error states
 * 
 * @example
 * ```typescript
 * const cancelWithdrawal = useCancelWithdrawalRequest()
 * 
 * const handleCancel = (withdrawalId: number) => {
 *   cancelWithdrawal.mutate(withdrawalId, {
 *     onSuccess: (response) => {
 *       toast.success(response.message)
 *     },
 *     onError: (error) => {
 *       toast.error("Không thể hủy yêu cầu rút tiền")
 *     }
 *   })
 * }
 * ```
 */
export const useCancelWithdrawalRequest = () => {
	const queryClient = useQueryClient()
	
	return useMutation({
		mutationFn: cancelWithdrawalRequest,
		onSuccess: () => {
			// Invalidate related queries to refresh data
			queryClient.invalidateQueries({ queryKey: ["affiliateBalance"] })
			queryClient.invalidateQueries({ queryKey: ["withdrawalRequests"] })
			queryClient.invalidateQueries({ queryKey: ["withdrawalDetail"] })
		},
		onError: (error) => {
			console.error("Withdrawal cancellation failed:", error)
		}
	})
}
